import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listJobs,
  getJob,
  createJob,
  updateJob,
  deleteJob,
  parseJobUrl,
  researchJob,
  dismissJobNoGo,
  dismissJobsNoGoBulk,
  restoreJobToInbox,
  restoreJobsToInboxBulk,
} from "@/api/jobs";
import type { Job, JobInput } from "@career-ops-amplify/shared";

const JOBS_QUERY_KEY = ["jobs"] as const;

export function useJobs(params?: {
  company?: string;
  search?: string;
  status?: string;
  pipelineId?: string;
  stage?: string;
  limit?: number;
  offset?: number;
  cursor?: string;
}) {
  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, params ?? {}],
    queryFn: () => listJobs(params),
    staleTime: 30_000,
  });
}

export function useJob(jobId: string | undefined) {
  return useQuery({
    queryKey: [...JOBS_QUERY_KEY, "detail", jobId],
    queryFn: () => getJob(jobId!),
    enabled: !!jobId,
    staleTime: 30_000,
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: JobInput) => createJob(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

export function useUpdateJob(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Partial<Job>) => updateJob(jobId, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => deleteJob(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

export function useParseJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, mode }: { jobId: string; mode?: "ai" | "offline" }) =>
      parseJobUrl(jobId, mode ?? "ai"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

export function useResearchJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, promptKind }: { jobId: string; promptKind?: string }) =>
      researchJob(jobId, { promptKind }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

export function useDismissJobNoGo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, reason }: { jobId: string; reason?: string }) =>
      dismissJobNoGo(jobId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

export function useDismissJobsNoGoBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobIds, reason }: { jobIds: string[]; reason?: string }) =>
      dismissJobsNoGoBulk(jobIds, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

export function useRestoreJobToInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => restoreJobToInbox(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}

export function useRestoreJobsToInboxBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobIds: string[]) => restoreJobsToInboxBulk(jobIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
}